import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  UpdateAssessmentActivityWithLinkedItemsMutation,
  UpdateAssessmentActivityWithLinkedItemsMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAssessmentActivityWithLinkedItemsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for updateActivity.
 * Uses explicit type instead of GraphQL MutationVariables to avoid
 * the codegen `Array<T> | T` issue with inline variables.
 */
type UpdateAssessmentActivityTRPCInput = {
  Id: string;
  ActivityType: Assessment_Activity_Type_Enum;
  ParentId: string;
  Status: Assessment_Activity_Status_Enum;
  Summary?: string | null;
  Title?: string | null;
  CompletionDate?: string | null;
  AssignedUser?: string | null;
  OriginalTimestamp?: string | null;
  LinkedItemIds: string[];
  CustomAttributeData?: CustomAttributeData | null;
  IsWizardAction?: boolean;
  // No `IsRCSA` / `RiskId`: not writable on update (COI-629 / COI-665) —
  // register membership and the risk association are fixed at create.
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
};

export const useUpdateAssessmentActivityWithLinkedItems = createMutationHook({
  name: 'updateAssessmentActivity',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.updateActivity.mutationOptions({ onSuccess }),
  // The codegen list-scalar variables widen to `T | T[]` — normalise them to
  // plain arrays for the tRPC procedure.
  mapInput: (
    variables: UpdateAssessmentActivityWithLinkedItemsMutationVariables
  ): UpdateAssessmentActivityTRPCInput => ({
    Id: variables.Id,
    ActivityType: variables.ActivityType,
    ParentId: variables.ParentId,
    Status: variables.Status,
    Summary: variables.Summary,
    Title: variables.Title,
    CompletionDate: variables.CompletionDate,
    AssignedUser: variables.AssignedUser,
    OriginalTimestamp: variables.OriginalTimestamp,
    LinkedItemIds:
      variables.LinkedItemIds == null
        ? []
        : Array.isArray(variables.LinkedItemIds)
          ? variables.LinkedItemIds
          : [variables.LinkedItemIds],
    CustomAttributeData: variables.CustomAttributeData,
    IsWizardAction: variables.IsWizardAction ?? undefined,
    OwnerUserIds:
      variables.OwnerUserIds == null
        ? []
        : Array.isArray(variables.OwnerUserIds)
          ? variables.OwnerUserIds
          : [variables.OwnerUserIds],
    OwnerGroupIds:
      variables.OwnerGroupIds == null
        ? []
        : Array.isArray(variables.OwnerGroupIds)
          ? variables.OwnerGroupIds
          : [variables.OwnerGroupIds],
  }),
  invalidate: (trpc) => [
    trpc.frontend.assessment.activityRegister.queryKey(),
    trpc.frontend.assessment.activityById.queryKey(),
    trpc.frontend.assessment.assessmentActivitiesByParentId.queryKey(),
    trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryKey(),
    trpc.frontend.assessment.register.queryKey(),
    // COI-471 parity: Apollo twin also evicts `internal_audit_report` and
    // `oversight_assessment`.
    trpc.frontend.internalAuditReport.pathKey(),
    trpc.frontend.oversight.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: {
    Id: string;
  }): UpdateAssessmentActivityWithLinkedItemsMutation => ({
    updateAssessmentActivityWithLinkedItems: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateAssessmentActivityWithLinkedItemsDocument,
  graphqlVariables: (
    variables: UpdateAssessmentActivityWithLinkedItemsMutationVariables
  ) => ({ ...variables }),
  evictFields: [
    'assessment_activity',
    'assessment',
    'oversight_assessment',
    'internal_audit_report',
  ],
});
